import React, { useEffect, useRef } from "react";
import {
  Modal,
  Tag,
  Typography,
  Divider,
  Empty,
  Statistic,
  Card,
  Col,
  Row,
} from "antd";
import {
  WarningOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import "./MissingTabsModal.scss";
import { getSubTabDisplayName, getTabDisplayName } from "../constants";
import { useSettings } from "../../custom-hooks/useSettings";
import {
  applySecondaryToggleOffInCache,
  buildMissingTabsToggleOffPayload,
  fetchPlanScopedTabs,
} from "./missingTabsSettingsUtils";

const { Title } = Typography;

const resolveMicrositeId = (aiWebsocketData) => {
  if (aiWebsocketData?.micrositeId) return aiWebsocketData.micrositeId;
  if (aiWebsocketData?.micrositeID) return aiWebsocketData.micrositeID;

  try {
    const aiDetails = JSON.parse(sessionStorage.getItem("aiDetails") || "{}");
    return aiDetails?.micrositeID || null;
  } catch {
    return null;
  }
};

const MissingTabsModal = ({ aiWebsocketData, open, onClose }) => {
  const { updateSettings } = useSettings();
  const appliedMissingTabsKeyRef = useRef(null);

  const missingTabs = aiWebsocketData?.missingTabs?.missingTabName ?? [];
  const missingSubTabs = aiWebsocketData?.missingTabs?.missingSubTabName ?? [];
  const missingTabsKey = missingTabs.join("|");
  const completedTabs = aiWebsocketData?.completedTabs?.completedTabName || [];
  const completedSubTabs =
    aiWebsocketData?.completedTabs?.completedSubTabName || [];

  const formatTabName = (str) => {
    return str
      ?.toLowerCase()
      ?.replace(/\b\w/g, (char) => char?.toUpperCase());
  };

  const totalCount =
    missingTabs?.length +
    missingSubTabs?.length +
    completedTabs?.length +
    completedSubTabs?.length;

  useEffect(() => {
    if (!open || !missingTabsKey || !aiWebsocketData) return;

    const micrositeId = resolveMicrositeId(aiWebsocketData);
    if (!micrositeId) return;

    const dedupeKey = `${micrositeId}:${missingTabsKey}`;
    if (appliedMissingTabsKeyRef.current === dedupeKey) return;

    let cancelled = false;

    const disableMissingTabs = async () => {
      try {
        const planScopedTabs = await fetchPlanScopedTabs(micrositeId, {
          force: true,
          includeAllTabs: true,
        });
        if (cancelled) return;

        const payload = buildMissingTabsToggleOffPayload(
          planScopedTabs,
          missingTabs,
        );
        if (!payload.length) return;

        appliedMissingTabsKeyRef.current = dedupeKey;
        applySecondaryToggleOffInCache(micrositeId, payload);
        await updateSettings(micrositeId, payload);
      } catch (error) {
        console.error("Failed to update settings for missing tabs", error);
      }
    };

    disableMissingTabs();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aiWebsocketData, missingTabsKey]);

  // const completedCount = completedTabs?.length + completedSubTabs?.length;

  // const completionPercentage = totalCount
  //   ? Math.round((completedCount / totalCount) * 100)
  //   : 0;

  const Section = ({ title, items, color, icon,missingTabsLength, type }) => {
    if (!items?.length) return null;

    return (
      <>
        <Divider />
        <Title level={5}>
          {icon} {title} ({missingTabsLength})
        </Title>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {items?.map((item) => (
            <Tag
              key={item}
              color={color}
              style={{
                padding: "6px 12px",
                borderRadius: 16,
                fontSize: 13,
              }}
            >
              {type === "subtabs" ? getSubTabDisplayName(item) : getTabDisplayName(formatTabName(item))}
            </Tag>
          ))}
        </div>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={"60%"}
      centered
      maskClosable={false}
      className="missing-tabs-modal"
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <WarningOutlined
            style={{
              color: "#faad14",
              fontSize: "22px",
            }}
          />

          <span
            style={{
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Site Tabs - {aiWebsocketData?.micrositeName}
          </span>
        </div>
      }
    >
      {!totalCount ? (
        <Empty description="No Data Found" />
      ) : (
        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            paddingRight: 8,
          }}
        >
          {/* <div style={{ marginBottom: "24px" }}>
            <Title level={5}>Site Tabs Progress</Title>
            <Progress percent={completionPercentage} />
          </div> */}

          {/* Summary Cards */}
          <Row
            gutter={[16, 16]}
            style={{ margin: "0px", marginBottom: "24px" }}
          >
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Completed Tabs"
                  value={completedTabs?.length}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Completed Sub Tabs"
                  value={completedSubTabs?.length}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Missing Tabs"
                  value={missingTabs?.length}
                  valueStyle={{ color: "#ff4d4f" }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Missing Sub Tabs"
                  value={missingSubTabs?.length}
                  valueStyle={{ color: "#fa8c16" }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Completed Sections */}
          <Section
            title="Completed Tabs"
            items={completedTabs}
            color="success"
            icon={<CheckCircleOutlined />}
            missingTabsLength={completedTabs?.length}
            type = "maintabs"
          />

          <Section
            title="Completed Sub Tabs"
            items={completedSubTabs}
            color="success"
            icon={<CheckCircleOutlined />}
            missingTabsLength={completedSubTabs?.length}
            type = "subtabs"
          />

          {/* Missing Sections */}
          <Section
            title="Missing Tabs"
            items={missingTabs}
            color="error"
            icon={<FolderOpenOutlined />}
            missingTabsLength={missingTabs?.length}
            type = "maintabs"
          />

          <Section
            title="Missing Sub Tabs"
            items={missingSubTabs}
            color="warning"
            icon={<AppstoreOutlined />}
            missingTabsLength={missingSubTabs?.length}
            type = "subtabs"
          />
        </div>
      )}
    </Modal>
  );
};

export default MissingTabsModal;
